import { Dispatch, SetStateAction, useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import NoData from '../../../../../components/NoData';
import OpacityAnimation from '../../../../../components/OpacityAnimation';
import { DadosNotImpl, PeriodType, PreAdmDashDataType, SelectedDashboardType } from '../../types';
import Header from '../Header';
import ImpAutCard from '../ImpAutCard';
import DownloadOtimizationModal from './components/DownloadOtimizationModal';
import ImplantationsCard from './components/ImplantationsCard';
import LastMonthsRoutingAmount from './components/LastMonthsRoutingAmount';
import SlaCard from './components/SlaCard';
import TotalConsultsCard from './components/TotalConsultsCard';
import TotalReclamationsCard from './components/TotalReclamationsCard';
import UfsCard from './components/UfsCard';
import WorkplacesCard from './components/WorkplacesCard';

interface IPreAdmDashboard {
  dashData: PreAdmDashDataType;
  selectedPeriod: PeriodType;
  setChangePeriodModalShow: Dispatch<SetStateAction<boolean>>;
  downloadPreAdmOtimizationModalShow: boolean;
  setDownloadPreAdmOtimizationModalShow: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  autImpData: DadosNotImpl[];
  loadImpAutData: (month?: number, year?: number) => Promise<void>;
  periodBeingSelectedAutImp: PeriodType;
  setPeriodBeingSelectedAutImp: Dispatch<SetStateAction<PeriodType>>;
  setSelectedPeriodAutImp: Dispatch<SetStateAction<PeriodType>>;
  changePeriodModalShow: boolean;
  selectedPeriodAutImp: PeriodType;
  selectedDashboard: SelectedDashboardType;
}

export default function PreAdmDashboard({
  dashData,
  selectedPeriod,
  setChangePeriodModalShow,
  downloadPreAdmOtimizationModalShow,
  setDownloadPreAdmOtimizationModalShow,
  setIsLoading,
  autImpData,
  loadImpAutData,
  periodBeingSelectedAutImp,
  setPeriodBeingSelectedAutImp,
  setSelectedPeriodAutImp,
  selectedPeriodAutImp,
  selectedDashboard,
}: IPreAdmDashboard) {
  const reversedConsultsHistoric = useMemo(() => dashData.listaTotaisPeriodo?.reverse(), [dashData.listaTotaisPeriodo]);
  return (
    <>
      <Header selectedPeriod={selectedPeriod} setChangePeriodModalShow={setChangePeriodModalShow} setDownloadPreAdmOtimizationModalShow={setDownloadPreAdmOtimizationModalShow} />

      {dashData.codigo !== 0 && (
        <>
          <Container>
            <Row xs={1} md={2} lg={2}>
              <Col>
                <OpacityAnimation delay={0.05} isHeight100>
                  <TotalConsultsCard total={dashData.totConsultas?.totalConsultas ?? 0} />
                </OpacityAnimation>
              </Col>
              <Col>
                <OpacityAnimation delay={0.2} isHeight100>
                  <SlaCard sla={dashData.tempoResposta ?? 0} />
                </OpacityAnimation>
              </Col>
            </Row>

            <Row xs={1} md={2} lg={4}>
              <Col>
                <OpacityAnimation delay={0.35} isHeight100>
                  <ImplantationsCard
                    totalConsults={dashData.totConsultas?.totalConsultas ?? 0}
                    totalImplantations={dashData.totImplantacoes?.totImplantadas ?? 0}
                  />
                </OpacityAnimation>
              </Col>
              <Col>
                <OpacityAnimation delay={0.5} isHeight100>
                  <TotalReclamationsCard
                    totalConsults={dashData.totConsultas?.totalConsultas ?? 0}
                    totalReclamations={dashData.totReclamacoes?.totReclamacoes ?? 0}
                    period={selectedPeriod}
                  />
                </OpacityAnimation>
              </Col>
              <Col>
                <OpacityAnimation delay={0.65} isHeight100>
                  <WorkplacesCard
                    consultsByWorkplaces={dashData.totConsultasLocTrab}
                    period={selectedPeriod}
                  />
                </OpacityAnimation>
              </Col>
              <Col>
                <OpacityAnimation delay={0.8} isHeight100>
                  <UfsCard
                    consultsByUf={dashData.totConsultasUfs}
                    period={selectedPeriod}
                  />
                </OpacityAnimation>
              </Col>
            </Row>

            <Row>
              <Col>
                <OpacityAnimation>
                  <LastMonthsRoutingAmount lastMonthsHistoric={reversedConsultsHistoric} />
                </OpacityAnimation>
              </Col>
            </Row>

            <Row>
              <Col>
                <OpacityAnimation>
                  <ImpAutCard
                    autImpData={autImpData}
                    loadImpAutData={loadImpAutData}
                    periodBeingSelectedAutImp={periodBeingSelectedAutImp}
                    setPeriodBeingSelectedAutImp={setPeriodBeingSelectedAutImp}
                    setSelectedPeriodAutImp={setSelectedPeriodAutImp}
                    selectedPeriodAutImp={selectedPeriodAutImp}
                    selectedDashboard={selectedDashboard}
                  />
                </OpacityAnimation>
              </Col>
            </Row>
          </Container>

          <DownloadOtimizationModal
            downloadModalShow={downloadPreAdmOtimizationModalShow}
            setDownloadModalShow={setDownloadPreAdmOtimizationModalShow}
            setIsLoading={setIsLoading}
          />
        </>
      )}

      {autImpData?.length !== 0 && dashData.codigo === 0 && (
        <Container>
          <Row>
            <Col>
              <OpacityAnimation>
                <ImpAutCard
                  autImpData={autImpData}
                  loadImpAutData={loadImpAutData}
                  periodBeingSelectedAutImp={periodBeingSelectedAutImp}
                  setPeriodBeingSelectedAutImp={setPeriodBeingSelectedAutImp}
                  setSelectedPeriodAutImp={setSelectedPeriodAutImp}
                  selectedPeriodAutImp={selectedPeriodAutImp}
                  selectedDashboard={selectedDashboard}
                />
              </OpacityAnimation>
            </Col>
          </Row>
        </Container>
      )}

      {dashData.codigo === 0 && (
        <NoData
          icon="emptyBox"
          label={(
            <>
                Não há dados de Pré Admissional para o período selecionado
            </>
          )}
        />
      )}
    </>
  );
}
